import React from 'react';
import { useFullscreenOverlay } from './FullscreenOverlayContext';
import { backdrop } from './fullscreenOverlay.styles';
import type { FullscreenOverlayBackdropProps } from './types';

/**
 * FullscreenOverlayBackdrop Component
 *
 * Semi-transparent backdrop that covers the entire viewport.
 * Optionally closes the overlay when clicked (click-outside-to-close behavior).
 *
 * @example
 * <FullscreenOverlay.Backdrop>
 *   <FullscreenOverlay.Content>...</FullscreenOverlay.Content>
 * </FullscreenOverlay.Backdrop>
 *
 * @example With click-outside disabled
 * <FullscreenOverlay.Backdrop closeOnBackdropClick={false}>
 *   <FullscreenOverlay.Content>...</FullscreenOverlay.Content>
 * </FullscreenOverlay.Backdrop>
 */
export const FullscreenOverlayBackdrop = ({
  children,
  closeOnBackdropClick = true,
}: FullscreenOverlayBackdropProps) => {
  const { close } = useFullscreenOverlay();

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Only close if the backdrop itself was clicked, not its children
    if (closeOnBackdropClick && event.target === event.currentTarget) {
      close();
    }
  };

  return (
    <div className={backdrop} onClick={handleBackdropClick} role="presentation">
      {children}
    </div>
  );
};
